import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import resolveImageUrl from "../../utils/imageUtils";
import { getUser } from "../../utils/auth";
import { getStarredProperties, toggleStarProperty } from "../../utils/starredProperty";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
  FEATURE_LABELS,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";

const RANKS = {
  Inspector2: 1,
  Inspector7: 2,
  Inspector4: 3,
  Inspector1: 4,
  Inspector3: 5,
  Inspector6: 6,
  Inspector5: 7,
  Inspector8: 8,
  Inspector9: 9,
};

const getEffectiveRank = (u) => {
  if (!u) return 0;
  const username = String(u?.username || "").trim().toLowerCase();
  const flagType = String(u?.flag_verification_type || "").trim().toLowerCase();
  if (username === "inspector9") return RANKS.Inspector9;
  if (username === "inspector6") return RANKS.Inspector6;
  if (username === "inspector1") return RANKS.Inspector1;
  if (username === "inspector2") return RANKS.Inspector8;
  if (username === "inspector3") return RANKS.Inspector3;
  if (flagType === "review_cv") return RANKS.Inspector6;
  if (flagType === "review_supervisor") return RANKS.Inspector9;
  if (flagType === "add_cv") return RANKS.Inspector1;
  if (flagType === "add_supervisor") return RANKS.Inspector8;
  if (flagType === "add_cv_rank5") return RANKS.Inspector5;
  if (flagType === "ai") return RANKS.Inspector6;
  if (flagType === "supervisor") return RANKS.Inspector9;
  const r = Number(u?.rank) || 0;
  return r;
};

const reviewedEndpoints = (uid) => {
  return {
    listInitial: `/inspector/propertyreviewed-s1d/${uid}`,
    listResolved: `/inspector/propertyreviewed-resolved-s1d/${uid}`,
    count: `/inspector/reviews/count-s1d/${uid}`,
  };
};

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const ITEMS_PER_PAGE = 12;

// Helper functions for area categories
const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const inferLandAreaSqYards = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  const rounded = Math.round(n);
  const isEffectivelyInteger = Math.abs(n - rounded) < 1e-6;
  if (n > 108000) return n / 9;
  if (n > 3000 && isEffectivelyInteger && rounded % 9 === 0) return n / 9;
  return n;
};

const inferCoveredAreaSqFt = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  if (n <= 300) return n * 9;
  return n;
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  console.log("getMLDisplayValue called with field:", field, "property:", property);
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        console.log("landarea realValue:", realValue, "ml:", ml, "bucket:", bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        console.log("coveredarea realValue:", realValue, "ml:", ml, "bucket:", bucket);
      }
      break;
  }

  console.log("getMLDisplayValue realValue:", realValue);
  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      const strVal = String(realValue ?? "");
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      console.log("getMLDisplayValue formatted:", formatted);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const areValuesDifferent = (adminVal, mlVal, field) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  const a = normalize(adminVal);

  if ((field === "landarea" || field === "coveredarea") && mlVal) {
    const range = parseRange(mlVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value;
        case "less":
          return a >= range.value;
        case "between":
          return a < range.min || a > range.max;
        case "exact":
          return Math.abs(a - range.value) > 1e-9;
      }
    }
  }

  const m = normalize(mlVal);
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};

const ReviewedS1D = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("reassessed");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [highlightPin, setHighlightPin] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [filter, setFilter] = useState("all");
  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const [user, setUser] = useState(getUser());
  const userId = user?.id;
  const [starredPins, setStarredPins] = useState([]);
  const [starredLoading, setStarredLoading] = useState(false);
  const [starredError, setStarredError] = useState(null);
  const [propertyTimeline, setPropertyTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Check for user changes and load starred properties
  useEffect(() => {
    const checkUser = () => {
      const currentUser = getUser();
      console.log('👤 reviewed.jsx: checkUser called, currentUser:', currentUser);
      setUser(currentUser);

      // Load starred properties when user is available
      if (currentUser?.id) {
        console.log('👤 User found, loading starred properties now...');
        loadStarred(currentUser.id);
      } else {
        console.log('👤 No user found, clearing starredPins');
        setStarredPins([]);
        setStarredLoading(false);
      }


    };

    const loadStarred = async (uid) => {
      console.log('📌 reviewed.jsx: Loading starred properties for current user, uid:', uid);
      setStarredLoading(true);
      try {
        const res = await getStarredProperties();
        console.log('📌 reviewed.jsx: getStarredProperties response:', res);
        if (res.success) {
          const normalizedPins = res.starredPins.map(normalizePin);
          console.log('📌 reviewed.jsx: Setting starredPins to:', normalizedPins);
          setStarredPins(normalizedPins);
        }
      } catch (err) {
        console.error("❌ reviewed.jsx: error loading starred properties", err);
        setStarredError(err);
      } finally {
        setStarredLoading(false);
      }
    };

    // Check on mount, when storage changes (other tabs), and when auth changes (same tab)
    checkUser();
    window.addEventListener('storage', checkUser);
    window.addEventListener('authChange', checkUser);

    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('authChange', checkUser);
    };
  }, []);



  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const navigate = useNavigate();

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setShowDetailModal(false);
    setRotation(0);
    highlightPropertyCard(pinToHighlight);
    // Clear the pin query parameter from the URL
    navigate({ search: "" }, { replace: true });
  };



  // Toggle star status
  const toggleStar = async (pin) => {
    console.log('⭐ reviewed.jsx toggleStar called with pin:', pin, 'type:', typeof pin);
    const normalizedPin = normalizePin(pin);
    console.log('⭐ reviewed.jsx normalizedPin:', normalizedPin);
    console.log('⭐ reviewed.jsx Current starredPins:', starredPins);

    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    const wasStarred = starredPins.includes(normalizedPin);
    const actionName = wasStarred ? "unstar" : "star";

    // Track the star/unstar action with PIN
    await trackAction(actionName, normalizedPin, null, {
      star_action: actionName,
      property_id: normalizedPin
    });

    console.log('⭐ reviewed.jsx wasStarred:', wasStarred);

    // Optimistic update
    setStarredPins(prev =>
      wasStarred
        ? prev.filter(p => p !== normalizedPin)
        : [...prev, normalizedPin]
    );
    console.log('⭐ reviewed.jsx After optimistic update, starredPins set to:',
      wasStarred
        ? starredPins.filter(p => p !== normalizedPin)
        : [...starredPins, normalizedPin]
    );

    try {
      const res = await toggleStarProperty(normalizedPin);
      console.log('⭐ reviewed.jsx toggleStarProperty response:', res);
      if (res.success && res.starredPins) {
        const normalizedFromApi = res.starredPins.map(normalizePin);
        console.log('⭐ reviewed.jsx Setting starredPins from API:', normalizedFromApi);
        setStarredPins(normalizedFromApi);
      }
    } catch (err) {
      console.error("❌ reviewed.jsx: error toggling star", err);
      toast.error("Failed to update starred property");
      // Revert optimistic update
      setStarredPins(prev =>
        wasStarred
          ? [...prev, normalizedPin]
          : prev.filter(p => p !== normalizedPin)
      );
      console.log('⭐ reviewed.jsx Reverted optimistic update, starredPins:',
        wasStarred
          ? [...starredPins, normalizedPin]
          : starredPins.filter(p => p !== normalizedPin)
      );
    }
  };

  const isStarred = (pin) => {
    const normalized = normalizePin(pin);
    const includes = starredPins.includes(normalized);
    console.log('⭐ reviewed.jsx isStarred check: pin=', pin, 'normalized=', normalized, 'starredPins=', starredPins, 'includes=', includes);
    return includes;
  };

  const [showReassessModal, setShowReassessModal] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [features, setFeatures] = useState([]);
  const [overallExplanation, setOverallExplanation] = useState("");
  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });
  const [referenceNumber, setReferenceNumber] = useState("");
  const [pt6IssueDate, setPt6IssueDate] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notReassessComment, setNotReassessComment] = useState("");
  const [isChangingFromDoNotReassess, setIsChangingFromDoNotReassess] = useState(false);
  const [initialAfterValues, setInitialAfterValues] = useState({}); // Track initial after values
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    setFeatures((prev) =>
      prev.map((f) => (f.key === "pt" ? { ...f, after: newPt5 } : f))
    );
  };

  const isCurrentlyReassessed =
    selectedProperty?.reassess === 1 || selectedProperty?.reassess === "1";

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };
  const [currentPage, setCurrentPage] = useState(1);

  const toDateInputValue = (val) => {
    if (!val) return "";
    const str = String(val).trim();
    if (!str) return "";
    const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const buildFeatures = (p) => {
    const wasPreviouslyReassessed = p.reassess == 1;
    const hasSeparateInspectorAssessment = Number(p.inspector_assessment_version) === 1;
    return [
      {
        key: "floor",
        label: "Floor",
        before: p.floor_admin || "",
        ml: getMLDisplayValue(p, "floor"),
        after: wasPreviouslyReassessed
          ? (hasSeparateInspectorAssessment ? p.inspector_floor ?? "" : p.floors_pred || p.floor_pred || "")
          : "",
        unit: ""
      },
      {
        key: "landuse",
        label: "Land Use",
        before: p.landuse_admin || "",
        ml: getMLDisplayValue(p, "landuse"),
        after: wasPreviouslyReassessed
          ? (hasSeparateInspectorAssessment ? p.inspector_landuse ?? "" : p.land_use_pred || p.landuse_pred || "")
          : "",
        unit: ""
      },
      {
        key: "landarea",
        label: "Land Area",
        before: p.landarea_admin || "",
        ml: getMLDisplayValue(p, "landarea"),
        after: wasPreviouslyReassessed
          ? (hasSeparateInspectorAssessment ? p.inspector_landarea ?? "" : p.landarea_combined_pred || "")
          : "",
        unit: "sq.ft"
      },
      {
        key: "coveredarea",
        label: "Covered Area",
        before: p.coveredarea_admin || "",
        ml: getMLDisplayValue(p, "coveredarea"),
        after: wasPreviouslyReassessed
          ? (hasSeparateInspectorAssessment ? p.inspector_coveredarea ?? "" : p.coveredarea_combined_pred || "")
          : "",
        unit: "sq.ft"
      },
      {
        key: "pt",
        label: "PT-5 Serial Number",
        before: " ",
        ml: "",
        after: wasPreviouslyReassessed ? (p.refno || p.pt5 || p.pt_5 || p.pt || "") : "",
        unit: ""
      },
    ];
  };

  const updateFeature = (i, field, value) => {
    const updated = [...features];
    const f = updated[i];
    let validatedValue = value;
    if (field === "after") {
      switch (f.key) {
        case "floor":
        case "landarea":
        case "coveredarea":
          if (/[^0-9.]/.test(value)) {
            toast.error("Please enter only numbers for this field!");
            validatedValue = value.replace(/[^0-9.]/g, "");
          }
          if ((validatedValue.match(/\./g) || []).length > 1) {
            validatedValue = validatedValue.slice(0, -1);
          }
          if (validatedValue) {
            const numVal = parseFloat(validatedValue);
            if (f.key === "landarea") {
              if (numVal < 0 || numVal > 108000) {
                toast.error("Land Area must be between 0 and 108,000 sq yards!");
              }
            } else if (f.key === "coveredarea") {
              if (numVal < 0 || numVal > 272000) {
                toast.error("Covered Area must be between 0 and 272,000 sq ft!");
              }
            }
          }
          break;
        case "landuse":
          if (/[0-9]/.test(value)) {
            toast.error("Please enter only letters for Land Use!");
            validatedValue = value.replace(/[0-9]/g, "");
          }
          break;
      }
    }
    updated[i][field] = validatedValue;
    setFeatures(updated);
  };

  const updateRowExplanation = (key, value) => {
    setRowExplanations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride, pt6IssueDateOverride } = {}) => {
    if (!selectedProperty) return;
    const activeFeatures = featuresOverride ?? features;
    const activePt6Date = pt6IssueDateOverride ?? pt6IssueDate;

    try {
      setSubmitting(true);

      if (overallExplanation) {
        await axios.post("/inspector/save-explanation", {
          property_id: selectedProperty.id,
          user_id: userId,
          overall_explanation: overallExplanation,
          row_explanations: rowExplanations
        });
      }

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");

      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      fd.append("floors_ml", selectedProperty.floors_ml || "");
      fd.append("landuse_ml", selectedProperty.landuse_ml || "");
      fd.append("landarea_ml", selectedProperty.landarea_ml || "");
      fd.append("coveredarea_ml", selectedProperty.coveredarea_ml || "");

      const preds = {
        floors_pred: selectedProperty.floors_pred || "",
        land_use_pred: selectedProperty.land_use_pred || "",
        landarea_combined_pred: selectedProperty.landarea_combined_pred || "",
        coveredarea_combined_pred: selectedProperty.coveredarea_combined_pred || "",
        refno: selectedProperty.refno || ""
      };
      const inspectorAssessment = {
        inspector_floor: "",
        inspector_landuse: "",
        inspector_landarea: "",
        inspector_coveredarea: "",
      };

      // Collect all feature changes for the comment!
      const featureChanges = [];

      activeFeatures.forEach(f => {
        const val = f.key === "pt" && refnoOverride
          ? refnoOverride
          : f.after?.toString().trim();
        const initialVal = initialAfterValues[f.key]?.toString().trim();
        const isModified = val !== initialVal;

        let finalVal;
        if (f.key === "pt") {
          if (!val || !isModified) {
            finalVal = refnoOverride || (isModified ? val : (selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || ""));
          } else {
            finalVal = val;
          }
        } else if (hasUsableValue(val)) {
          finalVal = val;
        } else if (hasUsableValue(initialVal)) {
          finalVal = initialVal;
        } else {
          finalVal = "";
        }

        if (isModified) {
          let displayName;
          switch (f.key) {
            case "floor": displayName = "Number of Floors"; break;
            case "landuse": displayName = "Land Use"; break;
            case "landarea": displayName = "Land Area"; break;
            case "coveredarea": displayName = "Covered Area"; break;
            case "pt": displayName = "PT-5 Serial Number"; break;
            default: displayName = f.key; break;
          }
          featureChanges.push(`${displayName} changed from "${initialVal || 'Not set'}" to "${finalVal || 'Not set'}"`);
        }

        switch (f.key) {
          case "floor": inspectorAssessment.inspector_floor = finalVal; break;
          case "landuse": inspectorAssessment.inspector_landuse = finalVal; break;
          case "landarea": inspectorAssessment.inspector_landarea = finalVal; break;
          case "coveredarea": inspectorAssessment.inspector_coveredarea = finalVal; break;
          case "pt": if (finalVal) preds.refno = finalVal; break;
        }
      });

      Object.entries(preds).forEach(([key, val]) => {
        fd.append(key, val);
      });
      Object.entries(inspectorAssessment).forEach(([key, val]) => {
        fd.append(key, val);
      });

      if (activePt6Date) {
        fd.append("pt6_issue_date", activePt6Date);
      }

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      // Track reassess/edit save action
      // Extract individual explanations
      let floorExp = "";
      let landuseExp = "";
      let landareaExp = "";
      let coveredareaExp = "";

      // Check rowExplanations first
      if (rowExplanations) {
        floorExp = rowExplanations.floor || "";
        landuseExp = rowExplanations.landuse || "";
        landareaExp = rowExplanations.landarea || "";
        coveredareaExp = rowExplanations.coveredarea || "";
      }

      // Then check features for explanations
      activeFeatures.forEach(feature => {
        if (feature.explanation) {
          switch (feature.key) {
            case "floor": floorExp = feature.explanation; break;
            case "landuse": landuseExp = feature.explanation; break;
            case "landarea": landareaExp = feature.explanation; break;
            case "coveredarea": coveredareaExp = feature.explanation; break;
          }
        }
      });

      // Track each explanation as a separate row
      if (overallExplanation) {
        await trackAction(
          "Overall Explanation",
          selectedProperty.pin,
          null,
          {
            overall_explanation: overallExplanation
          }
        );
      }
      if (floorExp) {
        await trackAction(
          "Number of Floors Explanation",
          selectedProperty.pin,
          null,
          {
            floor_explanation: floorExp
          }
        );
      }
      if (landuseExp) {
        await trackAction(
          "Land Use Explanation",
          selectedProperty.pin,
          null,
          {
            landuse_explanation: landuseExp
          }
        );
      }
      if (landareaExp) {
        await trackAction(
          "Land Area Explanation",
          selectedProperty.pin,
          null,
          {
            landarea_explanation: landareaExp
          }
        );
      }
      if (coveredareaExp) {
        await trackAction(
          "Covered Area Explanation",
          selectedProperty.pin,
          null,
          {
            coveredarea_explanation: coveredareaExp
          }
        );
      }

      // Track each feature change separately (use for...of to preserve order)
      for (const feature of activeFeatures) {
        const oldVal = initialAfterValues[feature.key]?.toString().trim() || "";
        const newVal = feature.after?.toString().trim() || "";
        if (oldVal !== newVal) {
          const featureName = FEATURE_LABELS[feature.key] || feature.label || feature.key;
          await trackAction(
            isCurrentlyReassessed ? `reviewed_edit_${feature.key}_updated` : `${feature.key}_updated`,
            selectedProperty.pin,
            null,
            {
              old_value: oldVal,
              new_value: newVal,
              comment_text: `${featureName} changed from "${oldVal}" to "${newVal}"`
            }
          );
        }
      }

      await trackAction("reassessed", selectedProperty.pin);
      toast.success("Reassessment updated!");

      setShowReassessModal(false);
      setShowRefModal(false);
      setShowDetailModal(false);
      setSelectedProperty(null);
      setReadyToSubmit(false);

      // const res = await axios.get(`/inspector/propertyreviewed-resolved/${userId}`);
      // setProperties(dedupeByPin(res.data || []));
      const res = await axios.get(reviewedEndpoints(userId).listResolved);
      const fetchBust1 = Date.now();
      setProperties(dedupeByPin(res.data || []).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust1 })));
    } catch (err) {
      console.error(err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        const updatedFeatures = activeFeatures.map((f) =>
          f.key === "pt" ? { ...f, after: newPt5 } : f
        );
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: updatedFeatures,
          refnoOverride: newPt5,
          pt6IssueDateOverride: activePt6Date,
        });
      })) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err, "Failed to update reassessment"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotReassess = async () => {
    if (!selectedProperty) return;
    try {
      setSubmitting(true);
      const trimmed = String(notReassessComment ?? "").trim();
      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("comment", trimmed);

      const fieldsToPreserve = [
        "circle", "occupationstatus", "location",
        "floor_admin", "landuse_admin", "landarea_admin", "coveredarea_admin", "admin_garv",
        "floors_ml", "landuse_ml", "landarea_ml", "coveredarea_ml",
        "floors_pred", "land_use_pred", "landarea_combined_pred", "coveredarea_combined_pred", "refno",
        "pt6_issue_date"
      ];

      fieldsToPreserve.forEach(field => {
        fd.append(field, selectedProperty[field] || "");
      });

      if (trimmed && userId && selectedProperty?.id) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: trimmed,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      // Track do not reassess action!
      await trackAction(
        isCurrentlyReassessed ? "reviewed_change_to_do_not_reassess" : "do_not_reassess",
        selectedProperty.pin,
        null,
        {
          comment_text: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided"
        }
      );

      toast.success("Updated to 'Do Not Reassess'");

      setShowNotReassessModal(false);
      setShowDetailModal(false);

      // const res = await axios.get(`/inspector/propertyreviewed-resolved/${userId}`);
      // setProperties(dedupeByPin(res.data || []));
      const res = await axios.get(reviewedEndpoints(userId).listResolved);
      const fetchBust2 = Date.now();
      setProperties(dedupeByPin(res.data || []).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust2 })));
    } catch (err) {
      console.error(err);
      toast.error(getPropertyUpdateErrorMessage(err, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!readyToSubmit || submitting || !selectedProperty) return;
    handleSubmitReassess();
    setReadyToSubmit(false);
  }, [readyToSubmit, submitting, selectedProperty]);

  const openReassessEditor = () => {
    if (!selectedProperty) return;
    const wasDoNotReassess = selectedProperty.reassess === 0 || selectedProperty.reassess === "0";
    const alreadyHasPt5 = pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]);
    setIsChangingFromDoNotReassess(wasDoNotReassess && !alreadyHasPt5);
    setReferenceNumber(alreadyHasPt5 || "");
    // setPt6IssueDate(selectedProperty.pt6_issue_date || "");
    setPt6IssueDate(toDateInputValue(selectedProperty.pt6_issue_date));
    const initialFeatures = buildFeatures(selectedProperty);
    setFeatures(initialFeatures);
    // Save initial after values
    const initialAfter = {};
    initialFeatures.forEach(f => {
      initialAfter[f.key] = f.after;
    });
    setInitialAfterValues(initialAfter);
    setOverallExplanation("");
    setRowExplanations({
      floor: "",
      landuse: "",
      landarea: "",
      coveredarea: ""
    });
    setShowDetailModal(false);
    setShowReassessModal(true);
  };

  const openNotReassessEditor = () => {
    if (!selectedProperty) return;
    setNotReassessComment(selectedProperty.inspector_overall_explanation || selectedProperty.comment || "");
    setShowDetailModal(false);
    setShowNotReassessModal(true);
  };

  const fetchPropertyTimeline = async (pin) => {
    try {
      setTimelineLoading(true);
      const res = await axios.get(`/properties/timeline/${encodeURIComponent(pin)}`);
      setPropertyTimeline(res.data?.timeline || []);
    } catch (err) {
      console.error("Failed to fetch property timeline:", err);
      setPropertyTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      setError(null);
      setLoading(true);
      // const res = await axios.get(`/inspector/propertyreviewed-resolved/${userId}`);
      // const props = dedupeByPin(res.data || []);
      // console.log("Fetched properties:", props);
      // setProperties(props);
      const res = await axios.get(reviewedEndpoints(userId).listResolved);
      const fetchBust = Date.now();
      const props = dedupeByPin(res.data || []).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust }));
      console.log("Fetched properties:", props);
      setProperties(props);
    } catch (err) {
      console.error("Error fetching reviewed properties:", err);
      setError(err.message || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchProperties();
  }, [userId]);

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (!properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );

    if (!matched) return;

    const isReassessed = matched.reassess === 1 || matched.reassess === "1";
    const tab = isReassessed ? "reassessed" : "not-reassessed";

    const toTime = (val) => {
      const t = val ? new Date(val).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    const sorted = properties
      .filter((item) =>
        tab === "reassessed"
          ? item.reassess === 1 || item.reassess === "1"
          : item.reassess === 0 || item.reassess === "0"
      )
      .sort((a, b) => {
        if (tab === "reassessed") {
          const at = toTime(a.reassessment_date) || toTime(a.inspector_explanation_created_at);
          const bt = toTime(b.reassessment_date) || toTime(b.inspector_explanation_created_at);
          if (bt !== at) return bt - at;
        } else {
          const at = toTime(a.inspector_explanation_created_at) || toTime(a.reassessment_date);
          const bt = toTime(b.inspector_explanation_created_at) || toTime(b.reassessment_date);
          if (bt !== at) return bt - at;
        }
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });

    const idx = sorted.findIndex(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );
    const targetPage = idx >= 0 ? Math.floor(idx / ITEMS_PER_PAGE) + 1 : 1;

    setActiveTab(tab);
    setCurrentPage(targetPage);
    setHighlightPin(normalizePin(matched.pin));
    setSelectedProperty(matched);
    setShowDetailModal(true);
    fetchPropertyTimeline(matched.pin);
    setAutoOpenedPin(targetPinFromQuery);
  }, [targetPinFromQuery, properties, autoOpenedPin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filter]);

  // Calculate base properties for the current tab (without star filter)
  const baseTabProperties = useMemo(() => {
    const base = properties.filter((item) =>
      activeTab === "reassessed"
        ? item.reassess === 1 || item.reassess === "1"
        : item.reassess === 0 || item.reassess === "0"
    );

    const toTime = (val) => {
      const t = val ? new Date(val).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    return base.sort((a, b) => {
      if (activeTab === "reassessed") {
        const at = toTime(a.reassessment_date) || toTime(a.inspector_explanation_created_at);
        const bt = toTime(b.reassessment_date) || toTime(b.inspector_explanation_created_at);
        if (bt !== at) return bt - at;
      } else {
        const at = toTime(a.inspector_explanation_created_at) || toTime(a.reassessment_date);
        const bt = toTime(b.inspector_explanation_created_at) || toTime(b.reassessment_date);
        if (bt !== at) return bt - at;
      }
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [properties, activeTab]);

  // Calculate starred count for the current tab
  const starredCountInTab = useMemo(() => {
    return baseTabProperties.filter(prop => isStarred(prop.pin)).length;
  }, [baseTabProperties, starredPins]);

  const activeFilter = "all";

  const filteredProperties = useMemo(() => {
    switch (activeFilter) {
      case "starred":
        return baseTabProperties.filter(prop => isStarred(prop.pin));
      case "unstarred":
        return baseTabProperties.filter(prop => !isStarred(prop.pin));
      default:
        return baseTabProperties;
    }
  }, [baseTabProperties, activeFilter, starredPins]);

  const totalItems = filteredProperties.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = filteredProperties.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  const reassessedCount = properties.filter(p => p.reassess === 1 || p.reassess === "1").length;
  const notReassessedCount = properties.filter(p => p.reassess === 0 || p.reassess === "0").length;

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />
      <Toaster />

      <div className="flex-1 p-4 md:p-6 pb-24 overflow-y-auto">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Reviewed Properties
              </h1>
              <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
                Hello!<br />
                These are the properties that have been reviewed by you. You can choose to edit any action that you have made for each property.
              </p>
              <p className="text-gray-600 mt-2 text-lg font-bold">
                Total: <span className="font-bold text-xl">{reassessedCount + notReassessedCount}</span> properties
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-lg shadow">
                  Showing <span className="font-semibold">{currentItems.length}</span> of{" "}
                  <span className="font-semibold">{filteredProperties.length}</span> properties
                </div>

                <button
                  onClick={fetchProperties}
                  disabled={loading}
                  className="p-2 bg-white hover:bg-gray-100 text-gray-600 rounded-lg shadow transition-colors disabled:opacity-50"
                  title="Refresh"
                  data-click-id={activeTab === "reassessed" ? "reviewed_reassessed_refresh" : "reviewed_not_reassessed_refresh"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-300 mb-6">
          <button
            className={`px-6 py-3 font-medium text-sm transition-colors ${activeTab === "reassessed"
              ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            onClick={() => setActiveTab("reassessed")}
            data-click-id="reviewed_reassessed_all"
          >
            Reassessed ({reassessedCount})
          </button>

          <button
            className={`px-6 py-3 font-medium text-sm transition-colors ${activeTab === "not-reassessed"
              ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            onClick={() => setActiveTab("not-reassessed")}
            data-click-id="reviewed_not_reassessed_all"
          >
            Not Reassessed ({notReassessedCount})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-lg text-gray-600">Loading properties...</p>
            </div>
          </div>
        ) : error && !loading ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-semibold text-red-800">Error Loading Properties</h3>
            </div>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchProperties}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow">
            <div className="text-6xl mb-6">
              🏠
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Properties Assigned
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              You don't have any properties assigned to you yet.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentItems.map((item) => {
                const isSelected = normalizePin(item.pin) && normalizePin(item.pin) === highlightPin;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    data-pin={normalizePin(item.pin)}
                    data-click-id={activeTab === "reassessed" ? "reviewed_reassessed_view_details" : "reviewed_not_reassessed_view_details"}
                    data-property-id={item.pin}
                    className={`bg-white shadow hover:shadow-lg overflow-hidden group relative transition-all rounded-xl ${isSelected ? "border-2 border-sky-500" : "border border-gray-200"
                      }`}
                    onClick={() => {
                      setSelectedProperty(item);
                      setShowDetailModal(true);
                      fetchPropertyTimeline(item.pin);
                      trackAction(activeTab === "reassessed" ? "reviewed_reassessed_view_details" : "reviewed_not_reassessed_view_details", item.pin);
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                        ✓
                      </div>
                    )}
                    {/*
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStar(item.pin);
                      }}
                      className="absolute top-3 right-3 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors"
                      title={isStarred(item.pin) ? "Unstar property" : "Star property"}
                    >
                      <svg
                        className={`w-5 h-5 transition-transform duration-300 ${isStarred(item.pin)
                          ? "fill-yellow-500 text-yellow-500"
                          : "fill-gray-300 text-gray-300 hover:fill-yellow-300 hover:text-yellow-300"
                          }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                    */}
                    <div className="h-48 bg-gray-100 rounded-t-xl overflow-hidden relative">
                      {item.image ? (
                        <img
                          src={appendCacheBuster(resolveImageUrl(item.image), item._imgBust)}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                          alt="Property"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <span className="text-gray-400 text-sm">No Image</span>
                        </div>
                      )}
                      <div className="absolute top-2 right-12">
                        <span
                          className={`px-2 py-1 text-xs rounded-full font-medium ${item.reassess == 1
                            ? "bg-green-500 text-white"
                            : "bg-red-500 text-white"
                            }`}
                        >
                          {item.reassess == 1 ? "✓ Reassessed" : "✗ Not Reassessed"}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h2 className="font-bold text-lg text-gray-900 truncate flex-1 mr-2">
                          {item.pin}
                        </h2>
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <button className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors">
                          View Details →
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm mr-40">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-semibold">{startIndex + 1}</span> to{" "}
                    <span className="font-semibold">{Math.min(endIndex, totalItems)}</span> of{" "}
                    <span className="font-semibold">{totalItems}</span> properties
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                      data-click-id={activeTab === "reassessed" ? "reviewed_reassessed_page_previous" : "reviewed_not_reassessed_page_previous"}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    <div className="flex items-center space-x-1">
                      {getPageNumbers().map((pageNum, index) => (
                        <React.Fragment key={index}>
                          {pageNum === '...' ? (
                            <span className="px-3 py-2 text-gray-400">...</span>
                          ) : (
                            <button
                              onClick={() => goToPage(pageNum)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                }`}
                            >
                              {pageNum}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 text-gray-700"
                      data-click-id={activeTab === "reassessed" ? "reviewed_reassessed_page_next" : "reviewed_not_reassessed_page_next"}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

          </>
        )}

        {/* DETAIL MODAL - Full screen with no scroll */}
        <AnimatePresence>
          {showDetailModal && selectedProperty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full h-full shadow-2xl p-6 relative overflow-y-auto"
              >
                {/* Close Button */}
                <button
                  onClick={closeDetailModal}
                  className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
                  data-click-id="reviewed_close_details"
                  data-property-id={selectedProperty.pin}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Header Info */}
                <div className="mb-8 pr-14">
                  <div className="space-y-6">
                    {/* PIN */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-gray-500 text-sm mb-1">PIN</p>
                      <p className="text-xl font-semibold text-gray-800">{selectedProperty.pin}</p>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-gray-500 text-md mb-3">Reassessment Status</p>
                      <span
                        className={`inline-flex items-center px-5 py-2.5 rounded-full text-base font-bold shadow-sm ${selectedProperty.reassess == 1
                          ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200"
                          : "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200"
                          }`}
                      >
                        {selectedProperty.reassess == 1 ? (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Reassessed
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Not Reassessed
                          </>
                        )}
                      </span>
                      {selectedProperty.reassess == 1 && selectedProperty.reassessment_date && (
                        <p className="mt-3 text-sm text-gray-600 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Reassessed At:{" "}
                          <span className="font-semibold text-gray-700">
                            {new Date(selectedProperty.reassessment_date).toLocaleString()}
                          </span>
                        </p>
                      )}
                    </div>

                    {/* Circle, PT-5, and PT-6 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-gray-500 text-sm mb-1">Circle</p>
                        <p className="text-lg font-semibold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-gray-500 text-sm mb-1">PT-5 Serial Number</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]) || "N/A"}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-gray-500 text-sm mb-1">PT-6 Issue Date</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {selectedProperty.pt6_issue_date
                            ? new Date(selectedProperty.pt6_issue_date).toLocaleDateString()
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property Image */}
                <div className="mb-6">
                  <div
                    className="relative group h-[500px] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer"
                    onClick={() => selectedProperty.image && setShowFullImage(true)}
                  >
                    {selectedProperty.image ? (
                      <>
                        <div
                          className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                          style={{ transform: `rotate(${rotation}deg)` }}
                        >
                          <img
                            src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                            className="max-w-full max-h-full object-contain"
                            alt="Property"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                        </div>
                        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                          <button
                            onClick={handleRotate}
                            className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                            title="Rotate Image"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          {rotation !== 0 && (
                            <button
                              onClick={handleSaveRotation}
                              disabled={savingImageRotation}
                              className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                              title="Save Rotation"
                            >
                              {savingImageRotation ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">No Image Available</span>
                    )}
                  </div>
                </div>

                {/* Basic Information Section */}
                <div className="mb-8 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                  <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center gap-2 pb-3 border-b border-gray-100">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { label: "PIN", value: selectedProperty.pin },
                      { label: "Circle", value: selectedProperty.circle || "N/A" },
                      { label: "Location", value: selectedProperty.location || "N/A" },
                      {
                        label: "PT-5 Serial Number",
                        value: selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "N/A"
                      },
                      {
                        label: "PT-6 Issue Date",
                        value: selectedProperty.pt6_issue_date
                          ? new Date(selectedProperty.pt6_issue_date).toLocaleDateString()
                          : "N/A"
                      },
                      {
                        label: "Status",
                        value: (
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${selectedProperty.reassess == 1
                            ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200"
                            : "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200"
                            }`}>
                            {selectedProperty.reassess == 1 ? "✓ Reassessed" : "✗ Not Reassessed"}
                          </span>
                        ),
                      },
                    ].map((item, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
                        <span className="text-gray-800 font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Three Tables Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Admin Data */}
                  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-4 border-b border-blue-100">
                      <h3 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3 3v-3m3-8a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Admin Data
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[
                        { label: "Floor", value: hasUsableValue(selectedProperty.floor_admin) ? selectedProperty.floor_admin : "N/A" },
                        { label: "Land Use", value: hasUsableValue(selectedProperty.landuse_admin) ? selectedProperty.landuse_admin : "N/A" },
                        { label: "Land Area", value: hasUsableValue(selectedProperty.landarea_admin) ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards` : "N/A" },
                        { label: "Covered Area", value: hasUsableValue(selectedProperty.coveredarea_admin) ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft` : "N/A" },
                      ].map((item, index) => (
                        <div key={index} className="px-5 py-4 flex flex-col gap-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
                          <span className="text-gray-800 font-medium">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ML (Computer Vision Prediction) */}
                  <div className="hidden">
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-5 py-4 border-b border-purple-100">
                      <h3 className="text-lg font-semibold text-purple-800 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Updated Assessment
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[
                        {
                          label: "Floor",
                          field: "floor"
                        },
                        {
                          label: "Land Use",
                          field: "landuse"
                        },
                        {
                          label: "Land Area",
                          field: "landarea"
                        },
                        {
                          label: "Covered Area",
                          field: "coveredarea"
                        },
                      ].map((item, index) => {
                        try {
                          const mlDisplay = getMLDisplayValue(selectedProperty, item.field);
                          return (
                            <div key={index} className="px-5 py-4 flex flex-col gap-1">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
                              <span className="text-purple-700 font-medium">{mlDisplay ?? "N/A"}</span>
                            </div>
                          );
                        } catch (error) {
                          console.error("Error rendering AI Prediction item:", error);
                          return (
                            <div key={index} className="px-5 py-4 flex flex-col gap-1">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
                              <span className="text-purple-700 font-medium">N/A</span>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>

                  {/* Inspector Changes / Updated Assessment */}
                  {selectedProperty.reassess == 1 && (
                    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-5 py-4 border-b border-green-100">
                        <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Updated Assessment
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {[
                          {
                            label: "Floor",
                            inspector: Number(selectedProperty.inspector_assessment_version) === 1
                              ? selectedProperty.inspector_floor
                              : selectedProperty.floors_pred ??
                                selectedProperty.floor_pred,
                            isArea: false,
                          },
                          {
                            label: "Land Use",
                            inspector: Number(selectedProperty.inspector_assessment_version) === 1
                              ? selectedProperty.inspector_landuse
                              : selectedProperty.land_use_pred ??
                                selectedProperty.landuse_pred,
                            isArea: false,
                          },
                          {
                            label: "Land Area",
                            inspector: Number(selectedProperty.inspector_assessment_version) === 1
                              ? selectedProperty.inspector_landarea
                              : selectedProperty.landarea_combined_pred,
                            isArea: true,
                          },
                          {
                            label: "Covered Area",
                            inspector: Number(selectedProperty.inspector_assessment_version) === 1
                              ? selectedProperty.inspector_coveredarea
                              : selectedProperty.coveredarea_combined_pred,
                            isArea: true,
                          },
                        ].map((item, index) => {
                          try {
                            const inspectorVal = hasUsableValue(item.inspector) ? String(item.inspector).trim() : null;

                            let finalDisplay;

                            if (inspectorVal) {
                              finalDisplay = inspectorVal;
                              if (item.isArea) {
                                const isRange = String(inspectorVal).toLowerCase().includes("greater") ||
                                  String(inspectorVal).toLowerCase().includes("less") ||
                                  String(inspectorVal).toLowerCase().includes("between");
                                if (!isRange) {
                                  finalDisplay = formatTwoDecimals(item.inspector) || inspectorVal;
                                }
                                if (item.label === "Land Area") {
                                  finalDisplay = `${finalDisplay} sq.yards`;
                                } else if (item.label === "Covered Area") {
                                  finalDisplay = `${finalDisplay} sq.ft`;
                                }
                              }
                            } else {
                              finalDisplay = "N/A";
                            }

                            return (
                              <div key={index} className="px-5 py-4 flex flex-col gap-1">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
                                <span
                                  className={`font-medium ${inspectorVal ? 'bg-yellow-100 text-yellow-900 px-3 py-1 rounded-lg border border-yellow-200' : 'text-green-700'}`}
                                >
                                  {finalDisplay}
                                </span>
                              </div>
                            );
                          } catch (error) {
                            console.error("Error rendering Inspector Changes item:", error);
                            return null;
                          }
                        }).filter(Boolean)}
                      </div>
                    </div>
                  )}
                </div>

                {/*
                  Activity History & Chat + inspector comments:
                  Not shown on Reviewed — that flow is for Flagged by Computer Vision only.
                  Code kept below (disabled) for future reference.
                */}
                {false && (
                  <div className="mb-8 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3 mb-6 border-b border-gray-100 pb-4">
                      <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Activity History & Chat
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {/* Display Inspector Comments First if available */}
                      {(selectedProperty.inspector_overall_explanation ||
                        selectedProperty.inspector_floor_explanation ||
                        selectedProperty.inspector_landuse_explanation ||
                        selectedProperty.inspector_landarea_explanation ||
                        selectedProperty.inspector_coveredarea_explanation) && (
                          <div className="flex justify-start">
                            <div className="max-w-[85%] border border-gray-100 rounded-2xl px-5 py-4 shadow-md bg-gradient-to-br from-gray-50 to-white text-gray-900">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <div className="text-xs font-bold tracking-wide text-gray-700 uppercase">
                                  Inspector comment
                                </div>
                                {selectedProperty.inspector_explanation_created_at && (
                                  <div className="text-[11px] text-gray-600/80">
                                    {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                              <div className="space-y-3">
                                {selectedProperty.inspector_overall_explanation && (
                                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                                    {selectedProperty.inspector_overall_explanation}
                                  </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                  {selectedProperty.inspector_floor_explanation && (
                                    <div key="floor" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Floor</div>
                                      <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_floor_explanation}</div>
                                    </div>
                                  )}
                                  {selectedProperty.inspector_landuse_explanation && (
                                    <div key="landuse" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Land Use</div>
                                      <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_landuse_explanation}</div>
                                    </div>
                                  )}
                                  {selectedProperty.inspector_landarea_explanation && (
                                    <div key="landarea" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Land Area</div>
                                      <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_landarea_explanation}</div>
                                    </div>
                                  )}
                                  {selectedProperty.inspector_coveredarea_explanation && (
                                    <div key="coveredarea" className="bg-white border border-red-100 rounded-xl p-3 shadow-sm">
                                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Covered Area</div>
                                      <div className="text-sm whitespace-pre-wrap">{selectedProperty.inspector_coveredarea_explanation}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      {/* Now the regular timeline */}
                      {timelineLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                          <span className="ml-3 text-gray-600">Loading activity...</span>
                        </div>
                      ) : propertyTimeline.length > 0 ? (
                        propertyTimeline.map((item) => {
                          const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                          const role = String(item.user_role || "").toLowerCase();
                          const isSupervisor = item.type === "supervisor" || role === "supervisor";
                          const isInspector = item.type === "inspector_explanation" || role === "inspector";
                          const isAction = item.type === "action";

                          const actionText = (() => {
                            if (!isAction) return "";
                            if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                            if (item.action === "ACCEPT_PROPERTY") return "Resolved (Accepted by Supervisor)";
                            if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                            return String(item.action || "Action");
                          })();

                          const rows = item?.rows || {};
                          const rowEntries = [
                            ["Floor", rows.floor],
                            ["Land Use", rows.landuse],
                            ["Land Area", rows.landarea],
                            ["Covered Area", rows.coveredarea],
                          ].filter(([, v]) => hasUsableValue(v));

                          if (isAction) {
                            return (
                              <div key={item.id} className="flex justify-center">
                                <div className="px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  {actionText}{createdAt ? ` • ${createdAt}` : ""}
                                </div>
                              </div>
                            );
                          }

                          const sideClass = isSupervisor ? "justify-start" : "justify-start";
                          const bubbleClass = isSupervisor
                            ? "bg-gradient-to-br from-red-50 to-white border-red-200 text-red-900"
                            : isInspector
                              ? "bg-gradient-to-br from-red-50 to-white border-red-200 text-red-900"
                              : "bg-gradient-to-br from-blue-50 to-white border-blue-200 text-blue-900";
                          const headerLabel = isSupervisor ? "Supervisor comment" : isInspector ? "Inspector comment" : "Comment";

                          return (
                            <div key={item.id} className={`flex ${sideClass}`}>
                              <div className={`max-w-[85%] border rounded-2xl px-5 py-4 shadow-md ${bubbleClass}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                  <div className="text-xs font-bold tracking-wide uppercase">
                                    {headerLabel}{item.user_name ? ` • ${item.user_name}` : ""}{item.stage ? ` • ${item.stage}` : ""}
                                  </div>
                                  {createdAt && <div className="text-[11px] opacity-75">{createdAt}</div>}
                                </div>
                                {item.type === "inspector_explanation" ? (
                                  <div className="space-y-3">
                                    {hasUsableValue(item.overall) && (
                                      <div className="text-sm leading-relaxed whitespace-pre-wrap">{item.overall}</div>
                                    )}
                                    {rowEntries.length > 0 && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                        {rowEntries.map(([k, v]) => (
                                          <div key={k} className="bg-white/80 border border-black/5 rounded-xl p-3 shadow-sm">
                                            <div className="text-xs font-semibold opacity-80 uppercase tracking-wide mb-1">{k}</div>
                                            <div className="text-sm whitespace-pre-wrap">{v}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{item.text || ""}</div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        !selectedProperty.inspector_overall_explanation &&
                          !selectedProperty.inspector_floor_explanation &&
                          !selectedProperty.inspector_landuse_explanation &&
                          !selectedProperty.inspector_landarea_explanation &&
                          !selectedProperty.inspector_coveredarea_explanation ? (
                          <div className="py-12 text-center text-gray-500">
                            <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <p>No comments or activity yet for this property.</p>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}

                {(() => {
                  const isSupervisorItem = (item) =>
                    (item?.type === "supervisor" ||
                      String(item?.user_role || "").toLowerCase() === "supervisor") &&
                    item?.type !== "action";

                  const supervisorFlag = propertyTimeline.find(
                    (item) =>
                      isSupervisorItem(item) &&
                      item.text &&
                      item.text !== "Property reassessed by inspector" &&
                      item.text !== "Property accepted by supervisor"
                  ) || propertyTimeline.find(
                    (item) =>
                      isSupervisorItem(item) &&
                      item.stage &&
                      item.stage.includes("Flagged by Supervisor")
                  );

                  const sections = [];

                  if (supervisorFlag) {
                    sections.push(
                      <div key="supervisor-flag" className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Supervisor {supervisorFlag.user_name || "Supervisor"} Flagged this property
                          </h3>
                          {supervisorFlag.created_at && (
                            <div className="text-sm text-gray-500">
                              {new Date(supervisorFlag.created_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">
                          {supervisorFlag.text || ""}
                        </div>
                      </div>
                    );
                  }

                  const hasInspectorComment =
                    selectedProperty.inspector_overall_explanation ||
                    selectedProperty.inspector_floor_explanation ||
                    selectedProperty.inspector_landuse_explanation ||
                    selectedProperty.inspector_landarea_explanation ||
                    selectedProperty.inspector_coveredarea_explanation ||
                    selectedProperty.comment;

                  const overallCommentText =
                    selectedProperty.inspector_overall_explanation ||
                    selectedProperty.comment;

                  if (hasInspectorComment) {
                    sections.push(
                      <div key="inspector-comments" className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">Inspector Comments</h3>
                          {selectedProperty.inspector_explanation_created_at && (
                            <div className="text-sm text-gray-500">
                              {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                            </div>
                          )}
                        </div>

                        {overallCommentText && (
                          <div className="mb-3">
                            <div className="text-sm font-semibold text-gray-700">Overall</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {overallCommentText}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedProperty.inspector_floor_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Floor</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_floor_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_landuse_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Land Use</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_landuse_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_landarea_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Land Area</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_landarea_explanation}
                              </div>
                            </div>
                          )}
                          {selectedProperty.inspector_coveredarea_explanation && (
                            <div className="border border-gray-200 rounded-lg p-3">
                              <div className="text-sm font-semibold text-gray-700">Covered Area</div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {selectedProperty.inspector_coveredarea_explanation}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return sections.length > 0 ? <>{sections}</> : null;
                })()}

                {/* Action Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  {/* {selectedProperty.status && (selectedProperty.status.toLowerCase() === "resolved" ||selectedProperty.status.toLowerCase() === "unresolved" ) ? ( */}
                  {selectedProperty.is_resolved_in_other_flow ? (
                    <>
                      {/*<div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                      <p className="text-yellow-800 font-medium">
                        {selectedProperty.status.toLowerCase() === "resolved" 
                          ? "This property has already been resolved by the supervisor. No further changes are allowed." 
                          : "This property has been flagged by the supervisor. Please edit it from the Flagged by Supervisor tab."}
                      </p>
                    </div>*/}
                    </>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={openReassessEditor}
                        className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                        data-click-id={isCurrentlyReassessed ? "reviewed_edit_reassess" : "reassess"}
                        data-property-id={selectedProperty.pin}
                      >
                        {isCurrentlyReassessed ? "Edit Reassessment" : "Change to Reassess"}
                      </button>

                      <button
                        onClick={openNotReassessEditor}
                        className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                        data-click-id={isCurrentlyReassessed ? "reviewed_change_to_do_not_reassess" : "do_not_reassess"}
                        data-property-id={selectedProperty.pin}
                      >
                        {isCurrentlyReassessed
                          ? "Change to Do Not Reassess"
                          : "Edit Do Not Reassess"}
                      </button>

                      <button
                        onClick={closeDetailModal}
                        className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
                        data-click-id="reviewed_close_details"
                        data-property-id={selectedProperty.pin}
                      >
                        Close Details
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Fullscreen Image Modal */}
        <AnimatePresence>
          {showFullImage && selectedProperty && selectedProperty.image && (
            <div
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
              onClick={() => {
                setShowFullImage(false);
                setRotation(0);
              }}
            >
              <div className="absolute top-6 right-6 flex space-x-4 z-10">
                <button
                  onClick={handleRotate}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Rotate Image"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {rotation !== 0 && (
                  <button
                    onClick={handleSaveRotation}
                    disabled={savingImageRotation}
                    className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                    title="Save Rotation"
                  >
                    {savingImageRotation ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowFullImage(false);
                    setRotation(0);
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                >
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <motion.div
                initial={{ scale: 0.9, rotate: 0 }}
                animate={{ scale: 1, rotate: rotation }}
                exit={{ scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                  className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
                  alt="Full Property"
                />
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* REASSESS MODAL */}
        <AnimatePresence>
          {showReassessModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 shadow-2xl w-full h-full overflow-y-auto"
              >
                <h2 className="text-2xl font-bold mb-4">Edit Reassessment</h2>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left font-medium">Feature</th>
                        <th className="p-3 text-left font-medium">Current (Admin)</th>
                        <th className="p-3 text-left font-medium">ML (Computer Vision Prediction)</th>
                        <th className="p-3 text-left font-medium">Updated</th>
                        <th className="p-3 text-left font-medium">Explanation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.filter((f) => f.key !== "pt").map((f) => {
                        const featureIndex = features.findIndex((x) => x.key === f.key);
                        const isAreaField = f.key === "landarea" || f.key === "coveredarea";
                        const unit = f.key === "landarea" ? "sq.ft" : "sq.ft";
                        return (
                          <tr className="border-b">
                            <td className="p-3 font-medium">{f.label}</td>
                            <td className="p-3">
                              <div className="bg-gray-50 p-2 rounded flex items-center gap-2">
                                {isAreaField
                                  ? (formatTwoDecimals(f.before) || "Not set")
                                  : (f.before || "Not set")}
                                {isAreaField && f.before && <span className="text-gray-500 text-sm">{unit}</span>}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="bg-gray-50 p-2 rounded flex items-center gap-2">
                                {isAreaField
                                  ? (formatTwoDecimals(f.ml) || f.ml || "N/A")
                                  : (f.ml || "N/A")}
                              </div>
                            </td>
                            <td className="p-3">
                              {f.key === "landuse" ? (
                                <select
                                  value={f.after || ""}
                                  onChange={(e) => {
                                    updateFeature(featureIndex, "after", e.target.value);
                                    const map = { Residential: "reviewed_edit_land_use_updated_residential", Mixed: "reviewed_edit_land_use_updated_mixed", Commercial: "reviewed_edit_land_use_updated_commercial" };
                                    const cid = map[e.target.value];
                                    if (cid) import("../../utils/clickTracking").then(({ trackAction }) => trackAction(cid, selectedProperty?.pin));
                                  }}
                                  className="w-full border border-gray-300 p-3 rounded"
                                  disabled={submitting}
                                  data-property-id={selectedProperty?.pin}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : isAreaField ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  className="w-full border border-gray-300 p-3 rounded"
                                  placeholder={`Enter ${f.label.toLowerCase()}...`}
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(featureIndex, "after", e.target.value)}
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                            <td className="p-3">
                              {isAreaField ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  className="w-full border border-gray-300 p-3 rounded"
                                  placeholder={`Explain ${f.label.toLowerCase()} change...`}
                                  value={rowExplanations[f.key] || ""}
                                  onChange={(e) => updateRowExplanation(f.key, e.target.value)}
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      PT-5 Serial Number <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => {
                        const val = e.target.value;
                        setReferenceNumber(val);
                        setFeatures((prev) =>
                          prev.map((f) => (f.key === "pt" ? { ...f, after: val } : f))
                        );
                      }}
                      className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter PT-5 Serial Number ..."
                      data-property-id={selectedProperty?.pin}
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      PT-6 Issue Date <span className="text-gray-400 font-normal"></span>
                    </label>
                    <input
                      type="date"
                      value={pt6IssueDate}
                      onChange={(e) => setPt6IssueDate(e.target.value)}
                      className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-property-id={selectedProperty?.pin}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block font-medium text-gray-700 mb-2">Overall Explanation</label>
                  <textarea
                    className="w-full border p-3 rounded-lg"
                    rows={4}
                    placeholder="Provide an overall explanation for the reassessment..."
                    value={overallExplanation}
                    onChange={e => setOverallExplanation(e.target.value)}
                    data-property-id={selectedProperty?.pin}
                  />
                </div>

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => {
                      if (isChangingFromDoNotReassess) {
                        setReferenceNumber("");
                        setShowReassessModal(false);
                        setShowRefModal(true);
                      } else {
                        handleSubmitReassess();
                      }
                    }}
                    className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button onClick={() => setShowReassessModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300" data-click-id="reassessment canceled">Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* DO NOT REASSESS MODAL */}
        <AnimatePresence>
          {showNotReassessModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Change to Do Not Reassess</h2>
                <textarea value={notReassessComment} onChange={e => setNotReassessComment(e.target.value)}
                  rows={4} placeholder="Enter reason / comments..." className="w-full p-3 border rounded mb-3" />
                {!notReassessComment && <p className="text-red-500 text-sm mb-3">Comment is required</p>}

                <div className="flex gap-3">
                  <button
                    onClick={handleNotReassess}
                    disabled={!notReassessComment || submitting}
                    className={`flex-1 px-4 py-2 rounded text-white ${notReassessComment ? "bg-gray-700 hover:bg-gray-800" : "bg-gray-400 cursor-not-allowed"
                      }`}
                    data-click-id="reason_for_not_reassessing_confirm"
                    data-property-id={selectedProperty.pin}
                  >
                    {submitting ? "Saving..." : "Confirm"}
                  </button>
                  <button
                    onClick={() => setShowNotReassessModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="reason_for_not_reassessing_cancel"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* REFERENCE MODAL */}
        <AnimatePresence>
          {showRefModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.85 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  {pt6IssueDate
                    ? "Edit PT-5 & PT-6 Details"
                    : isChangingFromDoNotReassess
                      ? "Enter PT-5 Serial Number (Required)"
                      : "Enter PT-5 & PT-6 Details"}
                </h2>

                {referenceNumber && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">PT-5 Serial:</span> {referenceNumber}
                    </p>
                  </div>
                )}

                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="PT-5 Serial Number"
                  className={`w-full p-3 border rounded mb-2 ${isChangingFromDoNotReassess && !referenceNumber.trim() ? "border-red-500" : "border-gray-300"}`}
                />
                {isChangingFromDoNotReassess && !referenceNumber.trim() && (
                  <p className="text-red-500 text-sm mb-3">PT-5 Serial Number is required when changing to reassess</p>
                )}

                <div className="mt-4">
                  <label className="block font-medium text-gray-700 mb-2">
                    PT-6 Issue Date <span className="text-gray-400"></span>
                  </label>
                  <input
                    type="date"
                    value={pt6IssueDate}
                    onChange={(e) => setPt6IssueDate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-gray-500">
                    Date when PT-6 was issued. Can be left blank and filled in later.
                  </p>
                </div>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      if (referenceNumber) {
                        setFeatures(prev =>
                          prev.map(f =>
                            f.key === "pt" ? { ...f, after: referenceNumber } : f
                          )
                        );
                      }
                      setShowRefModal(false);
                      setReadyToSubmit(true);
                    }}
                    disabled={submitting || (isChangingFromDoNotReassess && !referenceNumber.trim())}
                    className={`flex-1 px-4 py-2 rounded text-white ${(submitting || (isChangingFromDoNotReassess && !referenceNumber.trim()))
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                      }`}
                  >
                    {submitting ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => {
                      setShowRefModal(false);
                      setShowReassessModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="pt5_cancel"
                  >
                    Back
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <DuplicatePt5Modal
          isOpen={showDuplicatePt5Modal}
          onClose={closeDuplicatePt5Modal}
          onSave={saveDuplicatePt5Retry}
          submitting={submitting}
        />
      </div>
    </div>
  );
};

export default ReviewedS1D;
